/**
 * @file     ListingGate.tsx
 * @readme   ../CLAUDE.md
 * @purpose  The free-preview gate's parts. `ListingGateContext` tells the
 *           listing actions the gate is locked (they switch to the gated
 *           config) and lets Create an account reopen it. `ListingGateSheet`
 *           is the gate: one why-line quoting the real preview allowance,
 *           Google and Apple when their flags are on, then email, which hands
 *           over to the VOW registration sheet. `ListingGateCard` takes the
 *           place of the action buttons after the visitor closes the gate, so
 *           closing keeps them on the listing.
 */
'use client';

import { createContext, useContext, useState, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTranslation } from 'react-i18next';
import { GateNote, GatePrimaryButton, gateColors } from '@/components/gate/GateSheet';
import { SocialAuthButtons } from '@/components/gate/SocialAuthButtons';
import { FormConsentLine } from '@/components/lead-capture/FormConsentLine';
import { ListingSheet, SheetIcon, type SheetListing } from '@/components/lead-capture/ListingSheet';
import { FREE_PREVIEW_LIMIT } from '@/hooks/useListingsAccess';
import { CONTACT_EMAIL_PATTERN } from '@/services/leads/contactFields';
import { hubColors } from '@/components/hub/hubKitStyles';
import { layoutTokens, zLayers } from '@/design-system/tokens';
import { fontWeightTokens } from '@/design-system/tokens/typography';

export interface ListingGateValue {
  locked: boolean;
  /** Opens the gate again from the preview card or a Create an account button. */
  reopen: () => void;
}

export const ListingGateContext = createContext<ListingGateValue | null>(null);

/** Null outside the slug page's gate (quick views, curated listings). */
export function useListingGate(): ListingGateValue | null {
  return useContext(ListingGateContext);
}

function useGateCopy() {
  const { t } = useTranslation();
  return {
    title: t('listingGate.title', { defaultValue: 'See this listing with a free account' }),
    why: t('listingGate.why', {
      defaultValue: "You've viewed {{count}} listings. Create a free account to see this one.",
      count: FREE_PREVIEW_LIMIT,
    }),
  };
}

export function useSignInHref(): string {
  const pathname = usePathname();
  return `/sign-in?redirect=${encodeURIComponent(pathname || '/properties')}`;
}

const MAIL = 'M4 6h16v12H4zM4 7l8 6 8-6';
const LOCK = 'M7 11V8a5 5 0 0 1 10 0v3M6 11h12a1.5 1.5 0 0 1 1.5 1.5v7A1.5 1.5 0 0 1 18 21H6a1.5 1.5 0 0 1-1.5-1.5v-7A1.5 1.5 0 0 1 6 11z';

export function ListingGateSheet({
  open,
  onClose,
  listing,
  onEmail,
}: {
  open: boolean;
  onClose: () => void;
  listing: SheetListing;
  /** Continue with email: hands the address (when typed here) to registration. */
  onEmail: (email?: string) => void;
}) {
  const { t } = useTranslation();
  const copy = useGateCopy();
  const signInHref = useSignInHref();
  const isPhone = useMediaQuery('(max-width: 599.95px)');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(false);

  const continueWithEmail = () => {
    if (!CONTACT_EMAIL_PATTERN.test(email.trim())) {
      setEmailError(true);
      return;
    }
    onEmail(email.trim());
  };

  return (
    <ListingSheet
      open={open}
      onClose={onClose}
      listing={listing}
      title={copy.title}
      lead={copy.why}
      zIndex={zLayers.gate}
      testId="listing-gate"
    >
      <SocialAuthButtons divider={!isPhone} />
      {isPhone ? (
        <GatePrimaryButton onClick={() => onEmail()} startIcon={<SheetIcon d={MAIL} />}>
          {t('listingGate.email', { defaultValue: 'Continue with email' })}
        </GatePrimaryButton>
      ) : (
        <Box
          component="form"
          noValidate
          onSubmit={(event: React.FormEvent) => {
            event.preventDefault();
            continueWithEmail();
          }}
          sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
        >
          <TextField
            id="listing-gate-email"
            type="email"
            autoComplete="email"
            label={t('forms.contact.emailLabel', { defaultValue: 'Email' })}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setEmailError(false);
            }}
            error={emailError}
            helperText={emailError ? t('forms.contact.emailInvalid', { defaultValue: 'Enter an email address, like name@example.com.' }) : undefined}
            fullWidth
          />
          <GatePrimaryButton type="submit">{t('common.actions.continue', { defaultValue: 'Continue' })}</GatePrimaryButton>
        </Box>
      )}
      <FormConsentLine action={t('forms.consent.continuing', { defaultValue: 'continuing' })} />
      <GateNote>
        {t('listingGate.haveAccount', { defaultValue: 'Have an account?' })}{' '}
        <Box component="a" href={signInHref} sx={{ fontWeight: fontWeightTokens.medium, color: gateColors.link, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
          {t('listingActions.label.signIn', { defaultValue: 'Sign in' })}
        </Box>
      </GateNote>
    </ListingSheet>
  );
}

/** In place of the action buttons once the gate is closed. `bare` drops the
 *  card chrome where a host panel (the rail) already draws one. */
export function ListingGateCard({ anchorId, bare = false }: { anchorId?: string; bare?: boolean }) {
  const { t } = useTranslation();
  const gate = useListingGate();
  const copy = useGateCopy();
  const signInHref = useSignInHref();
  return (
    <Box
      id={anchorId}
      role="region"
      aria-label={t('listingGate.cardLabel', { defaultValue: 'Listing preview' })}
      data-listing-gate-card
      sx={{
        boxSizing: 'border-box',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 3,
        ...(bare
          ? null
          : { p: 6, borderRadius: `${layoutTokens.radius.lg}px`, border: `1px solid ${hubColors.borderSubtle}`, backgroundColor: hubColors.surfaceCard }),
      }}
    >
      <Box
        aria-hidden
        sx={{ width: 44, height: 44, borderRadius: `${layoutTokens.radius.sm}px`, backgroundColor: hubColors.chip, color: hubColors.chipText, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <SheetIcon d={LOCK} size={20} />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography component="p" sx={{ m: 0, fontSize: 18, fontWeight: fontWeightTokens.semibold, lineHeight: 1.3, letterSpacing: '-0.01em', color: hubColors.textPrimary }}>
          {copy.title}
        </Typography>
        <Typography component="p" sx={{ m: 0, fontSize: 14.5, lineHeight: 1.5, color: hubColors.textSecondary }}>
          {copy.why}
        </Typography>
      </Box>
      <GatePrimaryButton onClick={() => gate?.reopen()} data-action="create_account">
        {t('listingActions.label.createAccount', { defaultValue: 'Create an account' })}
      </GatePrimaryButton>
      <ButtonBase
        component="a"
        href={signInHref}
        data-action="sign_in"
        sx={{
          alignSelf: 'center',
          minHeight: 44,
          px: 2,
          borderRadius: `${layoutTokens.radius.sm}px`,
          fontFamily: 'inherit',
          fontSize: 15,
          fontWeight: fontWeightTokens.medium,
          color: hubColors.link,
          '&.Mui-focusVisible': { outline: `2px solid ${hubColors.interactiveFocusRing}`, outlineOffset: 2 },
        }}
      >
        {t('listingActions.label.signIn', { defaultValue: 'Sign in' })}
      </ButtonBase>
    </Box>
  );
}

export function ListingGateProvider({ value, children }: { value: ListingGateValue; children: ReactNode }) {
  return <ListingGateContext.Provider value={value}>{children}</ListingGateContext.Provider>;
}
